import { useMemo } from 'react';
import { useNavigation } from '../app/navigation';
import { FulfilmentBadge } from '../components/features/FulfilmentBadge';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Icon } from '../components/ui/Icon';
import { creditIconFor } from '../components/ui/currency';
import { Panel } from '../components/ui/Panel';
import { useGame } from '../game/store';
import { useSession } from '../state/session';
import {
  creditUnit,
  creditWord,
  formatCreditAmount,
  formatCredits,
  formatRelative,
} from '../lib/format';
import layout from '../styles/layout.module.css';
import styles from './ShopPage.module.css';

/**
 * How to earn credits.
 *
 * **Nothing is sold here, and nothing is claimed here.** Credits are a loyalty
 * reward for business a client does with the creator on the platforms where they
 * already sell. Spoyl never takes a payment, shows a price or a checkout, and —
 * since spend is only ever recorded by the model or the owner — gives the client
 * no way to say what they spent. This page shows what earns a reward; the credits
 * arrive when the creator records the purchase.
 */
export function ShopPage() {
  const { shop, coupons, orders, balance, naming } = useGame();
  const { navigate } = useNavigation();
  const { theme } = useSession();

  /** The creator's word for her money, mid-sentence — used all over this page. */
  const money = creditWord(naming);
  /** And her mark for it, which travels beside every figure on the page. */
  const creditIcon = creditIconFor(theme);

  const available = useMemo(() => coupons.filter((coupon) => !coupon.usedAt), [coupons]);

  return (
    <>
      <section className={styles.intro}>
        <div>
          <h2 className={styles.introTitle}>Earn {money}</h2>
          <p className={styles.introText}>
            {creditUnit(naming, 2)} are a thank-you for supporting the creator where they post. Do
            any of the things below, and your creator adds your {money} here once she has seen it.
            There is nothing to claim and nothing is ever bought inside Spoyl.
          </p>
        </div>
        <span className={styles.balance}>
          <Icon name={creditIcon} size={16} />
          {formatCredits(balance)}
        </span>
      </section>

      {shop.length === 0 ? (
        <EmptyState
          icon={creditIcon}
          title="Nothing set up yet"
          description={`Ways to earn ${money} will show up here as soon as they are announced.`}
        />
      ) : (
        <div className={layout.grid}>
          {shop.map((pack) => (
            <article
              key={pack.id}
              className={`${styles.pack} ${pack.featured ? styles.featured : ''}`}
            >
              {pack.featured && <span className={styles.ribbon}>Popular</span>}
              <h3 className={styles.packName}>{pack.name}</h3>
              <p className={styles.packCredits}>
                <Icon name={creditIcon} size={18} />+{formatCredits(pack.credits)}
              </p>
              {pack.requirement && (
                <p className={styles.requirement}>
                  <Icon name="check" size={13} />
                  {pack.requirement}
                </p>
              )}
              {pack.tagline && <p className={styles.packTagline}>{pack.tagline}</p>}
            </article>
          ))}
        </div>
      )}

      <div className={layout.split}>
        <Panel title="Earlier requests" subtitle="Newest first">
          {orders.length === 0 ? (
            <p className={layout.sectionHint}>
              Nothing here. Your {money} are added by your creator when you support her.
            </p>
          ) : (
            <ul className={styles.orders}>
              {orders.map((entry) => (
                <li key={entry.id} className={styles.order}>
                  <span className={styles.orderBody}>
                    <span className={styles.orderName}>{entry.packName}</span>
                    <span className={styles.orderMeta}>
                      +{formatCreditAmount(naming, entry.credits)} · {formatRelative(entry.at)}
                    </span>
                  </span>
                  <FulfilmentBadge status={entry.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Coupons"
          subtitle="Stackable, single use"
          actions={
            <Badge tone={available.length ? 'brand' : 'neutral'}>
              {available.length} available
            </Badge>
          }
        >
          {coupons.length === 0 ? (
            <p className={layout.sectionHint}>
              No coupons yet. They drop on the wheels and are handed out by the team.
            </p>
          ) : (
            <ul className={styles.couponList}>
              {coupons.map((coupon) => (
                <li key={coupon.id} className={coupon.usedAt ? styles.spent : undefined}>
                  <div className={styles.coupon}>
                    <span className={styles.couponPercent}>−{coupon.percent}%</span>
                    <span className={styles.couponLabel}>{coupon.label}</span>
                    <code className={styles.couponCode}>{coupon.code}</code>
                    {coupon.usedAt && <Badge tone="neutral">Used</Badge>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className={styles.capHint}>
            Quote a code on the creator's page when you buy — it comes off there, not here.
          </p>
        </Panel>
      </div>

      <Panel title="Not sure how this works?" subtitle="Four steps, start to finish">
        <Button variant="secondary" icon="bolt" onClick={() => navigate('howto')}>
          Read How To Play
        </Button>
      </Panel>
    </>
  );
}
